<script setup lang="ts">
const { getMerchantAttributes } = useLuxuryDesign();
const detailDesign = computed(() => getMerchantAttributes(props.merchantId));

const props = defineProps({
  merchantId: {
    type: String,
    required: true,
  },
  rarity: {
    type: String,
    required: true,
  },
  number: {
    type: Number,
    required: false,
  },
  copies: {
    type: Number,
    required: false,
  },
  lowSupplyThreshold: {
    type: Number,
    required: false,
  },
  level: {
    type: Number,
    required: false,
  },
  imageId: {
    type: String,
    required: true,
  },
  listed: {
    type: Boolean,
    required: false,
    default: false,
  },
  isNew: {
    type: Boolean,
    required: false,
    default: false,
  },
  isMinted: {
    type: Boolean,
    required: false,
    default: true,
  },
  mintExpiration: {
    type: String,
    required: false,
    default: "",
  },
  unitsLeft: {
    type: Number,
    required: false,
    default: 0,
  },
  isTimeRemaining: {
    type: Boolean,
    required: false,
    default: false,
  },
  isCraftable: {
    type: Boolean,
    required: false,
    default: false,
  },
  properties: {
    type: Object as PropType<Record<string, Property>>,
    required: false,
  },
  startDate: {
    type: String,
    required: false,
    default: null,
  },
});

const route = useRoute();
const animate = ref(false);
const isMinted = toRef(props.isMinted);
const show3dView = ref(false);
const image = ref<HTMLImageElement | null>(null);
const viewerContainer = ref<HTMLDivElement | null>(null);
const is3dAvailable = ref(false);
const cardDetail3dViewerRef = ref<any>(null);
const viewerReady = ref(false);

const showComingSoon = computed(() => {
  const now = new Date();
  if (props.startDate) {
    const start = new Date(props.startDate);
    if (start > now) return true;
  }
  return false;
});

const toggle3dView = () => {
  if (!show3dView.value) viewerReady.value = false;
  show3dView.value = !show3dView.value;
};

const toggle3dFullScreen = () => {
  if (cardDetail3dViewerRef.value) cardDetail3dViewerRef.value.goFullScreen();
};

const update3dViewerHeight = () => {
  if (image.value && viewerContainer.value) {
    viewerContainer.value.style.height = `${image.value.clientHeight}px`;
  }
};

const getMaterialsFromProperties = () => {
  if (!props.properties) return null;
  if (!props.properties.materials) return null;
  return props.properties.materials;
};

const getSceneUrlFromProperties = () => {
  if (!props.properties) return null;
  if (!props.properties.scene) return null;
  return props.properties.scene;
};

const isAppleDevice = computed(() => {
  const ua = window.navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua);
});

watch(
  () => route.query.minted,
  (value) => {
    if (value) {
      animate.value = true;
      setTimeout(() => {
        animate.value = false;
      }, 1000);
    }
  }
);

onMounted(async () => {
  if (await checkImageExists(props.imageId, "glb")) is3dAvailable.value = true;
});
</script>
<template>
  <div
    v-bind="detailDesign"
    class="relative xl:col-start-1 lg:row-span-3 xl:self-end w-full h-full border-b border-r-0 xl:border-b-0 border-primary-900 overflow-hidden collectible-card">
    <div
      v-if="
        lowSupplyThreshold &&
        number &&
        !show3dView &&
        number <= lowSupplyThreshold
      "
      class="absolute bottom-4 right-0 text-xs text-neutral-200 text-center font-bold w-fit px-4 py-1 rounded-tl-2xl rounded-bl z-20 collectible-card__supply"
      :class="
        number <= lowSupplyThreshold && copies && copies !== number
          ? 'bg-error-300'
          : 'bg-neutral-500'
      ">
      <template v-if="number <= lowSupplyThreshold">
        {{
          $t("main.cardCollections.unitsLeft", {
            units: number,
            copies: copies,
          })
        }}
      </template>
    </div>
    <div
      v-if="number === 0"
      class="absolute bottom-8 right-0 bg-error-300 text-xs text-neutral-200 font-bold text-center w-fit py-2 px-4 rounded-tl-2xl rounded-bl z-20">
      {{ $t("main.cardCollectible.soldOut") }}
    </div>
    <div
      v-else-if="isNew"
      class="absolute bottom-8 right-0 bg-error-300 text-xs text-neutral-200 font-bold text-center w-fit py-2 px-4 rounded-tl-2xl rounded-bl z-20">
      {{ $t("main.cardCollections.new") }}
    </div>
    <div
      v-else-if="listed && !show3dView"
      class="absolute bottom-8 right-0 bg-error-300 text-xs text-neutral-200 font-bold text-center w-fit py-2 px-4 rounded-tl-2xl rounded-bl z-20">
      {{ $t("main.cardCollections.listed") }}
    </div>
    <div
      v-else-if="show3dView && !isAppleDevice"
      @click="toggle3dFullScreen"
      class="z-20 absolute bottom-7 right-0 text-xs cursor-pointer text-neutral-200 font-bold text-center w-fit px-4 hover:opacity-75 transition-opacity duration-100 ease-[cubic-bezier(0,0,.2,1)]">
      <BaseIcon
        name="fullscreen"
        class="w-10 h-10 transition-transform duration-100 ease-[cubic-bezier(0,0,.2,1)] hover:scale-110" />
    </div>
    <div v-if="isTimeRemaining" class="absolute bottom-7 left-4">
      <NuxtIcon name="mdi:lock" class="w-9 h-9 text-neutral-300 z-1" />
    </div>
    <div v-else-if="!isMinted" class="">
      <div
        class="absolute top-8 left-4 flex items-center gap-1 z-10 text-xs"
        v-if="new Date(mintExpiration).getFullYear() < 2050">
        <NuxtIcon name="mdi:clock" class="w-4 h-4 text-neutral-300 z-1" />
        <p
          class="text-secondary-200 font-bold text-center py-2 rounded-tl-2xl rounded-br-2xl rounded-tr rounded-bl">
          {{ $t("main.pendingMints.expires") }}:
        </p>
        <CommonCountdown :datetime="mintExpiration" class="text-white z-1" />
      </div>
      <div
        class="absolute bottom-4 left-4 w-fit max-w-[375px] font-medium text-neutral-300 flex items-center gap-2 bg-neutral-500 rounded-sm z-20 mr-4">
        <div class="flex items-start gap-2 py-2 pl-3 pr-3">
          <NuxtIcon
            name="mdi:warning"
            class="w-4 h-4 shrink-0 text-warning-300" />
          <i18n-t
            tag="p"
            class="text-warning-300 text-xs pt-0.5"
            keypath="main.cardCollections.mintNotice"
            scope="global">
            <template #previewOnly>
              <span class="font-bold">{{
                $t("main.cardCollections.previewOnly")
              }}</span>
            </template>
          </i18n-t>
        </div>
      </div>
    </div>
    <div
      v-if="is3dAvailable && isMinted"
      @click="toggle3dView"
      class="z-20 absolute cursor-pointer bottom-6 left-4 font-bold text-center w-fit always-rounded-md bg-[#1E192E] hover:opacity-80 border-solid border text-white border-white merchant-3d-toggle">
      <div class="flex items-center pl-4 pr-2 py-[0.15rem]">
        <p class="z-20 text-sm">{{ show3dView ? "2D" : "3D" }}</p>
        <BaseIcon name="3dcube" class="w-4 h-4 ml-2 mr-1 merchant-3d-icon" />
      </div>
    </div>
    <template v-if="!is3dAvailable">
      <img
        v-show="!show3dView"
        :src="getLargeItemImage(imageId)"
        alt="full item image"
        class="w-full h-full object-cover rounded-lg xl:rounded-0 transition-all duration-1000 ease-in-out"
        :class="!isMinted || animate ? 'saturate-0 opacity-50' : ''"
        @error="
          (e: any) => ((e.target as HTMLImageElement).src = getLargeItemImage())
        " />
    </template>
    <template v-else>
      <img
        ref="image"
        v-show="!show3dView"
        :src="getLargeItemImage(imageId)"
        alt="full item image"
        class="w-full h-full object-cover rounded-lg xl:rounded-0 transition-all duration-1000 ease-in-out"
        :class="!isMinted || animate ? 'saturate-0 opacity-50' : ''"
        @error="
        (e: any) => ((e.target as HTMLImageElement).src = getLargeItemImage())
      "
        @load="update3dViewerHeight" />
      <div
        ref="viewerContainer"
        v-show="show3dView"
        class="w-full h-full object-cover rounded-lg xl:rounded-0 transition-all duration-1000 ease-in-out">
        <CardDetail3dViewer
          ref="cardDetail3dViewerRef"
          v-if="show3dView"
          :image-id="imageId"
          :materials="getMaterialsFromProperties()"
          :scene="getSceneUrlFromProperties()" />
      </div>
    </template>
  </div>
</template>
